// components/AwsPricingSync.jsx — tab Sync & Config.
//
// Menampilkan status sync per (job, region) dan mengizinkan admin menjalankan
// sync manual serta mengubah kurs / VAT / jam per bulan.
//
// STATUS DITAMPILKAN APA ADANYA, termasuk 'empty'. Job yang mengembalikan nol
// produk BUKAN kesuksesan: data-transfer di ap-southeast-3 menghasilkan 0 baris
// dan tetap berstatus 'empty' — kalau ditampilkan hijau bersama yang lain,
// katalog yang berlubang akan terlihat lengkap.
//
// Sync EC2 satu region memakan ~40 detik dan Savings Plan ~12 menit, jadi
// tombolnya per job dan diberi peringatan durasi, bukan satu tombol "sync semua"
// yang tampak menggantung.
import React, { useCallback, useEffect, useState } from 'react';
import { api } from '../context/AuthContext';
import { useLang } from '../context/LanguageContext';
import {
  RefreshCw, Loader2, AlertTriangle, CheckCircle2, MinusCircle, Save, Clock, Database,
} from 'lucide-react';

const STATUS_STYLE = {
  success: { cls: 'text-emerald-700 bg-emerald-50 border-emerald-200', Icon: CheckCircle2 },
  empty:   { cls: 'text-amber-700 bg-amber-50 border-amber-200',       Icon: MinusCircle },
  error:   { cls: 'text-red-700 bg-red-50 border-red-200',             Icon: AlertTriangle },
};

export default function AwsPricingSync({ config, tiers, isAdmin, onConfigSaved }) {
  const { t } = useLang();
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [running, setRunning] = useState(null);
  const [result, setResult] = useState(null);

  const [edit, setEdit] = useState({});
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await api.get('/api/aws/pricing/status');
      setStatus(res.data);
      setError(null);
    } catch (e) {
      setError(e?.response?.data?.error || e.message);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const runSync = async (jobKey, region) => {
    const label = `${jobKey} · ${region}`;
    const warn = jobKey === 'savings-plan'
      ? t('awsp.sync.confirmSp')
      : t('awsp.sync.confirm');
    if (!window.confirm(`${warn}\n\n${label}`)) return;
    setRunning(label);
    setResult(null);
    try {
      const res = await api.post('/api/aws/pricing/sync',
        { jobs: [jobKey], regions: [region], includeSp: jobKey === 'savings-plan' },
        { timeout: 1800000 });
      setResult(res.data);
      await load();
    } catch (e) {
      setError(e?.response?.data?.error || e.message);
    } finally {
      setRunning(null);
    }
  };

  const saveConfig = async () => {
    if (!Object.keys(edit).length) return;
    setSaving(true);
    setSaved(null);
    try {
      const res = await api.put('/api/aws/pricing/config', { config: edit });
      setSaved(res.data.changed || []);
      setEdit({});
      if (onConfigSaved) onConfigSaved();
    } catch (e) {
      setError(e?.response?.data?.error || e.message);
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center gap-2 text-gray-500 text-sm p-6">
        <Loader2 className="w-4 h-4 animate-spin" /> {t('awsp.loading')}
      </div>
    );
  }

  const meta = status?.meta || [];
  const jobs = status?.jobs || [];
  const regions = (status?.regions || []).map(r => r.code);

  return (
    <div className="space-y-4">
      {error && (
        <div className="rounded-lg border border-red-300 bg-red-50 p-3 text-sm text-red-800 flex items-start gap-2">
          <AlertTriangle className="w-4 h-4 mt-0.5 shrink-0" /> <span>{error}</span>
        </div>
      )}

      {/* Ringkasan */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
        <Stat label={t('awsp.sync.products')} value={(status?.totals?.products || 0).toLocaleString('en-US')} />
        <Stat label={t('awsp.sync.terms')} value={(status?.totals?.terms || 0).toLocaleString('en-US')} />
        <Stat label={t('awsp.sync.jobsCount')} value={String(jobs.length)} />
        <Stat label={t('awsp.sync.running')} value={status?.running ? t('awsp.yes') : t('awsp.no')} />
      </div>

      {result && (
        <div className="rounded-lg border border-emerald-300 bg-emerald-50 p-3 text-sm text-emerald-900">
          <p className="font-medium">{result.message}</p>
          <p className="text-xs mt-1">{result.diffText}</p>
        </div>
      )}

      {/* Matriks job × region */}
      <div className="rounded-lg border border-gray-200 bg-white overflow-hidden">
        <div className="px-3 py-2 border-b border-gray-100 flex items-center gap-2 text-sm font-medium text-gray-700">
          <Database className="w-4 h-4 text-emerald-600" /> {t('awsp.sync.jobStatus')}
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-xs">
            <thead className="bg-gray-50 text-gray-500">
              <tr>
                <th className="px-2 py-1.5 text-left font-medium">{t('awsp.sync.job')}</th>
                <th className="px-2 py-1.5 text-left font-medium">{t('awsp.region')}</th>
                <th className="px-2 py-1.5 text-right font-medium">{t('awsp.sync.products')}</th>
                <th className="px-2 py-1.5 text-right font-medium">{t('awsp.sync.terms')}</th>
                <th className="px-2 py-1.5 text-right font-medium">{t('awsp.sync.duration')}</th>
                <th className="px-2 py-1.5 text-left font-medium">{t('awsp.sync.lastSync')}</th>
                <th className="px-2 py-1.5 text-left font-medium">{t('awsp.sync.status')}</th>
                {isAdmin && <th className="px-2 py-1.5" />}
              </tr>
            </thead>
            <tbody>
              {jobs.map(job => regions.map(region => {
                const m = meta.find(x => x.job_key === job.key && x.region === region);
                const st = m?.status || null;
                const style = STATUS_STYLE[st] || null;
                const label = `${job.key} · ${region}`;
                return (
                  <tr key={label} className="border-t border-gray-100">
                    <td className="px-2 py-1.5">
                      <span className="font-mono">{job.key}</span>
                      <span className="block text-gray-400">{job.label}</span>
                    </td>
                    <td className="px-2 py-1.5 font-mono">{region}</td>
                    <td className="px-2 py-1.5 text-right font-mono">
                      {m ? (m.product_count || 0).toLocaleString('en-US') : '—'}
                    </td>
                    <td className="px-2 py-1.5 text-right font-mono">
                      {m ? (m.term_count || 0).toLocaleString('en-US') : '—'}
                    </td>
                    <td className="px-2 py-1.5 text-right font-mono text-gray-500">
                      {m ? `${((m.duration_ms || 0) / 1000).toFixed(1)}s` : '—'}
                    </td>
                    <td className="px-2 py-1.5 text-gray-500 whitespace-nowrap">{m?.last_sync_at || '—'}</td>
                    <td className="px-2 py-1.5">
                      {!m && <span className="text-gray-400">{t('awsp.sync.never')}</span>}
                      {m && style && (
                        <span className={`inline-flex items-center gap-1 px-1.5 py-0.5 rounded border ${style.cls}`}>
                          <style.Icon className="w-3 h-3" /> {st}
                        </span>
                      )}
                      {m && !style && <span className="text-gray-500">{st}</span>}
                      {m?.error_text && (
                        <span className="block text-red-600 mt-0.5">{m.error_text}</span>
                      )}
                    </td>
                    {isAdmin && (
                      <td className="px-2 py-1.5 text-right">
                        <button
                          onClick={() => runSync(job.key, region)}
                          disabled={!!running}
                          className="px-2 py-1 rounded border border-gray-300 text-gray-600 hover:bg-gray-50 disabled:opacity-40 inline-flex items-center gap-1"
                        >
                          {running === label
                            ? <Loader2 className="w-3 h-3 animate-spin" />
                            : <RefreshCw className="w-3 h-3" />}
                          {t('awsp.sync.run')}
                        </button>
                      </td>
                    )}
                  </tr>
                );
              }))}
            </tbody>
          </table>
        </div>
        <p className="px-3 py-2 border-t border-gray-100 text-xs text-gray-500">
          {t('awsp.sync.emptyNote')}
        </p>
      </div>

      {/* Config */}
      <div className="rounded-lg border border-gray-200 bg-white overflow-hidden">
        <div className="px-3 py-2 border-b border-gray-100 flex items-center gap-2 text-sm font-medium text-gray-700">
          <Clock className="w-4 h-4 text-emerald-600" /> {t('awsp.sync.config')}
        </div>
        <div className="divide-y divide-gray-100">
          {(config?.raw || []).map(row => (
            <div key={row.key} className="px-3 py-2 grid grid-cols-1 sm:grid-cols-3 gap-2 items-center">
              <div>
                <p className="text-xs font-mono text-gray-800">{row.key}</p>
                <p className="text-xs text-gray-500">{row.description}</p>
              </div>
              <div>
                <input
                  value={edit[row.key] !== undefined ? edit[row.key] : row.value || ''}
                  onChange={e => setEdit(prev => ({ ...prev, [row.key]: e.target.value }))}
                  disabled={!isAdmin}
                  className="w-full px-2 py-1.5 rounded border border-gray-300 text-sm font-mono disabled:bg-gray-100"
                />
              </div>
              <p className="text-xs text-gray-400">
                {row.updated_at} · {row.updated_by}
              </p>
            </div>
          ))}
        </div>
        {isAdmin && (
          <div className="px-3 py-2 border-t border-gray-100 flex items-center gap-2">
            <button
              onClick={saveConfig}
              disabled={saving || !Object.keys(edit).length}
              className="px-3 py-1.5 rounded bg-emerald-600 text-white text-sm font-medium hover:bg-emerald-700 disabled:opacity-40 inline-flex items-center gap-2"
            >
              {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
              {t('awsp.save')}
            </button>
            {saved && (
              <span className="text-xs text-emerald-700">
                {t('awsp.sync.savedKeys').replace('{keys}', saved.join(', ') || '—')}
              </span>
            )}
          </div>
        )}
      </div>

      {/* Tier Support */}
      <div className="rounded-lg border border-gray-200 bg-white overflow-hidden">
        <div className="px-3 py-2 border-b border-gray-100 text-sm font-medium text-gray-700">
          {t('awsp.sync.supportTiers')}
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-xs">
            <thead className="bg-gray-50 text-gray-500">
              <tr>
                <th className="px-2 py-1.5 text-left font-medium">{t('awsp.calc.supportPlan')}</th>
                <th className="px-2 py-1.5 text-right font-medium">{t('awsp.sync.tierFrom')}</th>
                <th className="px-2 py-1.5 text-right font-medium">{t('awsp.sync.tierTo')}</th>
                <th className="px-2 py-1.5 text-right font-medium">%</th>
                <th className="px-2 py-1.5 text-right font-medium">{t('awsp.sync.min')}</th>
              </tr>
            </thead>
            <tbody>
              {(tiers || []).map((r, i) => (
                <tr key={i} className="border-t border-gray-100">
                  <td className="px-2 py-1.5 font-mono">{r.plan}</td>
                  <td className="px-2 py-1.5 text-right font-mono">${Number(r.tier_from).toLocaleString('en-US')}</td>
                  <td className="px-2 py-1.5 text-right font-mono">
                    {r.tier_to === null ? '∞' : `$${Number(r.tier_to).toLocaleString('en-US')}`}
                  </td>
                  <td className="px-2 py-1.5 text-right font-mono">{r.percent}</td>
                  <td className="px-2 py-1.5 text-right font-mono">${Number(r.min_usd).toLocaleString('en-US')}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <p className="px-3 py-2 border-t border-gray-100 text-xs text-gray-500">
          {t('awsp.sync.marginalNote')}
        </p>
      </div>
    </div>
  );
}

function Stat({ label, value }) {
  return (
    <div className="rounded-lg border border-gray-200 bg-white p-3">
      <p className="text-xs text-gray-500">{label}</p>
      <p className="text-lg font-semibold text-gray-800 font-mono">{value}</p>
    </div>
  );
}
